import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, Home, Phone, Mail, Calendar, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [pollingCount, setPollingCount] = useState(0);

  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');
  const paymentParam = searchParams.get('payment');

  // Poll for payment status if coming from Stripe
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) return;
      
      try {
        const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('paid');
          // Get updated booking
          if (booking?.id) {
            const bookingResponse = await axios.get(`${API}/bookings/${booking.id}`);
            setBooking(bookingResponse.data);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        return false;
      }
    };

    // Poll payment status
    if (sessionId && pollingCount < 5 && paymentStatus !== 'paid') {
      const timer = setTimeout(async () => {
        const isPaid = await checkPaymentStatus();
        if (!isPaid) {
          setPollingCount(prev => prev + 1);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [sessionId, pollingCount, paymentStatus, booking?.id]);

  // Get booking details
  useEffect(() => {
    const getBookingFromSession = async () => {
      if (sessionId) {
        try {
          // First check payment status to get booking info
          const statusResponse = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
          if (statusResponse.data.payment_status === 'paid') {
            setPaymentStatus('paid');
          }
        } catch (error) {
          console.error('Error:', error);
        }
      }
      
      if (bookingId) {
        try {
          const response = await axios.get(`${API}/bookings/${bookingId}`);
          setBooking(response.data);
        } catch (error) {
          console.error('Error fetching booking:', error);
        }
      }
      
      if (paymentParam === 'success') {
        setPaymentStatus('paid');
      } else if (paymentParam === 'pending') {
        setPaymentStatus('pending');
      }
      
      setLoading(false);
    };

    getBookingFromSession();
  }, [bookingId, sessionId, paymentParam]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Loading booking details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900/20 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Card */}
        <div className="bg-slate-800 rounded-3xl shadow-2xl shadow-black/50 overflow-hidden border border-slate-700">
          {/* Header */}
          <div className={`p-8 text-center ${paymentStatus === 'paid' ? 'bg-green-600' : 'bg-amber-500'}`}>
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
              paymentStatus === 'paid' ? 'bg-green-500' : 'bg-amber-400'
            }`}>
              {paymentStatus === 'paid' ? (
                <CheckCircle2 className="w-12 h-12 text-white" />
              ) : (
                <Clock className="w-12 h-12 text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {paymentStatus === 'paid' ? 'Booking Confirmed!' : 'Booking Received!'}
            </h1>
            <p className="text-white/90">
              {paymentStatus === 'paid' 
                ? 'Your payment was successful. We will contact you to confirm details.'
                : 'We have received your booking request. We will contact you shortly.'
              }
            </p>
          </div>

          {/* Booking Details */}
          <div className="p-8">
            {booking && (
              <div className="space-y-6">
                {/* Reference Number */}
                <div className="text-center p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                  <p className="text-sm text-slate-400 mb-1">Booking Reference</p>
                  <p className="text-xl font-mono font-bold text-white">{booking.id?.slice(0, 8).toUpperCase()}</p>
                </div>

                {/* Trip Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-500" />
                    Trip Details
                  </h3>
                  
                  <div className="grid gap-3">
                    <div className="p-3 bg-green-600/20 rounded-lg border border-green-500/30">
                      <p className="text-xs text-green-400 font-medium mb-1">PICKUP</p>
                      <p className="text-sm text-white">{booking.pickup_address}</p>
                    </div>
                    <div className="p-3 bg-red-600/20 rounded-lg border border-red-500/30">
                      <p className="text-xs text-red-400 font-medium mb-1">DROP-OFF</p>
                      <p className="text-sm text-white">{booking.dropoff_address}</p>
                    </div>
                  </div>
                </div>

                {/* Schedule & Service */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-slate-400">Date & Time</span>
                    </div>
                    <p className="font-semibold text-white">{booking.pickup_date}</p>
                    <p className="text-red-400">{booking.pickup_time}</p>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                    <p className="text-sm text-slate-400 mb-2">Service Type</p>
                    <p className="font-semibold text-white capitalize">
                      {booking.service_type?.replace('_', ' ')}
                    </p>
                    <p className="text-sm text-slate-400 capitalize">
                      {booking.trip_type?.replace('_', ' ')}
                    </p>
                  </div>
                </div>

                {/* Price */}
                <div className="p-4 bg-red-600/20 rounded-xl border border-red-500/30">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-slate-400">Total Amount</p>
                      <p className="text-sm text-slate-400">{booking.distance_miles} miles</p>
                    </div>
                    <p className="text-3xl font-bold text-red-500">
                      ${booking.estimated_price?.toFixed(2)}
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-red-500/30">
                    <div className="flex items-center gap-2">
                      {paymentStatus === 'paid' ? (
                        <>
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <span className="text-green-400 font-medium">Payment Confirmed</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5 text-amber-500" />
                          <span className="text-amber-400 font-medium">Payment Pending</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="p-4 bg-slate-900 rounded-xl text-white border border-slate-700">
                  <h4 className="font-semibold mb-3">Need Help?</h4>
                  <div className="space-y-2">
                    <a href="tel:+12534877444" className="flex items-center gap-3 text-red-400 hover:text-red-300 transition-colors">
                      <Phone className="w-4 h-4" />
                      +1 (253) 487-7444
                    </a>
                    <a href="mailto:info@marcopolonemt.com" className="flex items-center gap-3 text-red-400 hover:text-red-300 transition-colors">
                      <Mail className="w-4 h-4" />
                      info@marcopolonemt.com
                    </a>
                  </div>
                </div>
              </div>
            )}

            {!booking && (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <p className="text-slate-300 mb-4">
                  Your booking has been submitted. Please check your email for confirmation.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/" className="flex-1">
                <Button 
                  data-testid="back-home-btn"
                  variant="outline" 
                  className="w-full h-12 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/booking" className="flex-1">
                <Button 
                  data-testid="book-another-btn"
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
                >
                  Book Another Ride
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
